import { themingGetDominantColorExperience } from './experiences.tsx';

const SAMPLE_POINTS = 60;

export const SELECT_UNSPLASH_IMAGE = 'SELECT_UNSPLASH_IMAGE';
export const SELECT_CUSTOM_IMAGE = 'SELECT_CUSTOM_IMAGE';
export const UPLOAD_CUSTOM_IMAGE = 'UPLOAD_CUSTOM_IMAGE';

// Enum to indicate which action the user took, that triggered the dominant color calculation
// Used for perf metrics only
type UserAction =
	| typeof SELECT_UNSPLASH_IMAGE
	| typeof SELECT_CUSTOM_IMAGE
	| typeof UPLOAD_CUSTOM_IMAGE;

export const rgbToHex = (r: number, g: number, b: number): string => {
	const toHex = (n: number) => {
		const hex = n.toString(16);
		return hex.length === 1 ? `0${hex}` : hex;
	};
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const getDominantColor = (imageUrl: string, userAction: UserAction): Promise<string> => {
	themingGetDominantColorExperience.start();

	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'Anonymous';

		img.onload = () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				themingGetDominantColorExperience.abort();
				return reject(new Error('Failed to load canvas'));
			}

			canvas.width = img.width;
			// Only use the top third of the image
			canvas.height = img.height / 3;
			ctx.drawImage(img, 0, 0, img.width, img.height);
			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
			const data = imageData.data;

			const totalPixels = imageData.width * imageData.height;
			const step = Math.max(1, Math.floor(totalPixels / SAMPLE_POINTS));
			let r = 0;
			let g = 0;
			let b = 0;
			let count = 0;

			for (let i = 0; i < data.length; i += 4 * step) {
				r += data[i];
				g += data[i + 1];
				b += data[i + 2];
				count++;
			}

			r = Math.floor(r / count);
			g = Math.floor(g / count);
			b = Math.floor(b / count);

			const hex = rgbToHex(r, g, b);

			themingGetDominantColorExperience.success({ metadata: { userAction } });
			resolve(hex);
		};

		img.onerror = () => {
			themingGetDominantColorExperience.abort();
			reject(new Error('Failed to load image'));
		};

		img.src = imageUrl;
	});
};

export const getDominantColorByMediaFile = async (file: Blob): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = async (event) => {
			if (!event.target?.result || typeof event.target.result !== 'string') {
				reject(new Error('Failed to read file data'));
				return;
			}

			const dominantColor = await getDominantColor(event.target.result, UPLOAD_CUSTOM_IMAGE);
			resolve(dominantColor);
		};

		reader.onerror = () => {
			reject(new Error('Failed to read file'));
		};

		// Read the file as a data URL
		reader.readAsDataURL(file);
	});
};
